import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { toError } from 'src/app/classes/shared/logs';
import { Pop } from 'src/app/classes/shared/pop';
import { TrackerConfigTool } from 'src/app/classes/shared/tracker-config.tool';
import { TrackerTemplateEditor } from 'src/app/classes/shared/tracker-template-editor';
import { Project } from 'src/app/interfaces/api/project';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { ButtonComponent } from 'ui-library';
import { TrackerConfigFormComponent } from '../tracker-config-form/tracker-config-form.component';
import { ResourcesModalComponent } from '../resources-modal/resources-modal.component';

// Adds a device already configured: same "pick a type, optionally a template, configure" flow
// as the device's own editor (see TrackerTemplateEditor), with the type left unlocked.
@Component({
  selector: 'app-trackers-modal',
  templateUrl: './trackers-modal.component.html',
  imports: [
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    CommonModule,
    TranslateModule,
    MatToolbarModule,
    MatSelectModule,
    ButtonComponent,
    TrackerConfigFormComponent,
  ],
  standalone: true,
  styleUrls: ['./trackers-modal.component.scss']
})

export class TrackersModalComponent implements OnInit, OnDestroy {
  tracker: any = {};
  destroy$ = new Subject<void>();
  project: Project | null = null;

  readonly editor = new TrackerTemplateEditor();
  nameError: string = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public trackerService: TrackerService,
    private trackerConfigTool: TrackerConfigTool,
    private dialog: MatDialog,
    private pop: Pop,
    private dialogRef: MatDialogRef<ResourcesModalComponent>,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.tracker = this.data.tracker;
    if (this.data.currentProject)
      this.tracker.project = this.data.currentProject.id || this.data.currentProject;

    this.trackerService.getConfigs().pipe(takeUntil(this.destroy$)).subscribe((configs: TrackerConfig[]) => {
      this.editor.configs = configs || [];
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Saving as a template alone never adds the device - offers to add it right away.
  onSaveAsTemplate() {
    this.editor.saveAsTemplate(() =>
      this.pop.confirm(this.translate.instant('trackers.add_device_after_template_confirm'), (confirm: any) => {
        if (confirm.ok) this.saveTracker();
      })
    );
  }

  // The device's own name lives outside the embedded form (hideName - that field is only ever
  // about naming a standalone reusable TrackerConfig, not this tracker), so it needs its own
  // check here before the form's "Enregistrer" actually creates anything.
  saveTracker() {
    const trimmed = this.tracker.name?.trim();
    if (!trimmed) {
      this.nameError = this.translate.instant('trackers.name_required');
      return;
    }
    this.nameError = '';
    this.tracker.name = trimmed;

    const { type, settings, offlineDelay } = this.editor.draft;

    this.trackerConfigTool.createTrackerWithSettings(
      { ...this.tracker, type }, settings, offlineDelay, this.editor.appliedTemplate?.id
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.pop.toast(this.translate.instant("trackers.add_success"), "success", 4000, "add");
        this.dialog.closeAll();
      },
      error: err => {
        console.error(err);
        this.pop.error(toError(err));
      }
    });
  }

  closeDialog(result: any = null): void {
    this.dialogRef.close(result);
  }
}
